/**
 * Laws Compliance Hook
 *
 * Fetches and manages Platform Laws compliance state with
 * auto-refresh and audit trigger support.
 *
 * Phase 3.5-C: Operational Intelligence
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchComplianceState, triggerComplianceAudit } from '../api/complianceApi';
import type { LawsComplianceState, AuditTriggerResponse } from '../types/compliance';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

export interface UseLawsComplianceOptions {
  refreshInterval?: number;
  fetchOnMount?: boolean;
}

export interface UseLawsComplianceResult {
  data: LawsComplianceState | null;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  triggerAudit: () => Promise<AuditTriggerResponse | null>;
  isAuditing: boolean;
  lastUpdated: Date | null;
}

export function useLawsCompliance(
  authFetch: AuthFetch,
  options: UseLawsComplianceOptions = {},
): UseLawsComplianceResult {
  const { refreshInterval = 60000, fetchOnMount = true } = options;

  const [data, setData] = useState<LawsComplianceState | null>(null);
  const [isLoading, setIsLoading] = useState(fetchOnMount !== false);
  const [error, setError] = useState<Error | null>(null);
  const [isAuditing, setIsAuditing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const isMounted = useRef(true);
  const authFetchRef = useRef(authFetch);
  authFetchRef.current = authFetch;
  const dataRef = useRef<LawsComplianceState | null>(null);

  const fetchData = useCallback(async () => {
    // Only show loading skeleton on initial fetch (no existing data)
    if (!dataRef.current) {
      setIsLoading(true);
    }
    setError(null);
    try {
      const state = await fetchComplianceState(authFetchRef.current);
      if (isMounted.current) {
        setData(state);
        dataRef.current = state;
        setLastUpdated(new Date());
      }
    } catch (err) {
      if (isMounted.current) {
        setError(err instanceof Error ? err : new Error('Failed to fetch compliance state'));
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, []);

  const triggerAudit = useCallback(async (): Promise<AuditTriggerResponse | null> => {
    setIsAuditing(true);
    try {
      const result = await triggerComplianceAudit(authFetchRef.current);
      // Refresh data after audit completes
      await fetchData();
      return result;
    } catch (err) {
      if (isMounted.current) {
        setError(err instanceof Error ? err : new Error('Failed to trigger audit'));
      }
      return null;
    } finally {
      if (isMounted.current) {
        setIsAuditing(false);
      }
    }
  }, [fetchData]);

  useEffect(() => {
    if (fetchOnMount) {
      fetchData();
    }
  }, [fetchOnMount, fetchData]);

  useEffect(() => {
    if (refreshInterval <= 0) return;
    const id = setInterval(fetchData, refreshInterval);
    return () => clearInterval(id);
  }, [refreshInterval, fetchData]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  return { data, isLoading, error, refresh: fetchData, triggerAudit, isAuditing, lastUpdated };
}

export default useLawsCompliance;
